import { toast } from "react-toastify";
import { createContext, useContext, useState } from "react";
import { Outlet, redirect, useLoaderData, useNavigate, type LoaderFunction } from "react-router";

import { Api, Types } from "@/services";
import { checkToastThemeOption } from "@/utils";
import type { IUser } from "@/helpers/interfaces";

import { BigSidebar, DashboardWrapper, Navbar, SmallSidebar } from "@/components";

export const dashboardLoader: LoaderFunction = async () => {
  try {
    const { data } = await Api.CurrentUser();
    return data;
  } catch (_err) {
    return redirect("/");
  }
};

interface DashboardContextType {
  user: IUser;
  sidebar: boolean;
  isDarkTheme: boolean;
  toggleSidebar: () => void;
  toggleTheme: () => void;
  logoutUser: () => void;
}

const DashboardContext = createContext<DashboardContextType>({} as DashboardContextType);

export interface DashboardLayoutProps {
  isDarkThemeEnabled: boolean;
}

const DashboardLayout = ({ isDarkThemeEnabled }: DashboardLayoutProps) => {
  const navigate = useNavigate();
  const [sidebar, setSidebar] = useState(false);
  const [isDarkTheme, setIsDarkTheme] = useState(isDarkThemeEnabled);
  const { user } = useLoaderData<Types.Response.CurrentUser>();

  const toggleTheme = () => {
    const newDarkTheme = !isDarkTheme;
    setIsDarkTheme(prev => !prev);
    document.body.classList.toggle("dark-theme", newDarkTheme);
    localStorage.setItem("dark-theme", String(newDarkTheme));
  };

  const toggleSidebar = () => setSidebar(prev => !prev);

  const logoutUser = async () => {
    localStorage.removeItem("t$o@k!en*");
    navigate("/");
    toast.success("Logging out...", checkToastThemeOption());
  };

  return (
    <DashboardContext.Provider value={{ user, sidebar, isDarkTheme, toggleSidebar, toggleTheme, logoutUser }}>
      <DashboardWrapper>
        <main className="dashboard">
          <SmallSidebar />
          <BigSidebar />
          <div>
            <Navbar />
            <div className="dashboard-page">
              <Outlet context={{ user }} />
            </div>
          </div>
        </main>
      </DashboardWrapper>
    </DashboardContext.Provider>
  );
};

export const useDashboardContext = () => useContext(DashboardContext);

export default DashboardLayout;
